import React from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../context/AuthContext";
import { formatDate } from "../lib/dateFormat";
import { useNavigate } from "react-router-dom";
import Growie from "./Growie";

/**
 * The club's trial, said out loud.
 *
 * The trial gives everything the Basic plan gives; the only thing that runs out
 * is time. So this counts days rather than seats, and when the time is up it
 * says the one thing a club admin needs to hear first: nothing was deleted.
 *
 * The locked state is firm but not punitive. It does not shout, it does not
 * threaten, and it does not hide the club's own data behind it — it explains
 * that the work is paused and gives one button that reaches a person.
 */
export default function DemoBanner() {
  const { t } = useTranslation();
  const { clubAccess } = useAuth();
  const navigate = useNavigate();

  if (!clubAccess || clubAccess.state === "paid") return null;

  const { state, daysLeft, trialEndsAt, clubName } = clubAccess;

  /**
   * The button no longer sends a request and waits for a person: it opens the
   * page where the club reads the contract and activates the plan itself. What
   * used to be a queue is now a decision the club can finish on its own.
   */
  const ask = () => navigate("/dashboard/admin-club/activare");

  if (state === "locked") {
    return (
      <div className="p-5 mb-4 border bg-white border-slate-200 rounded-2xl">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start">
          <Growie pose="think" className="w-16 h-16 shrink-0" />
          <div className="min-w-0">
            <h2 className="text-base font-bold text-slate-800">
              {t("trial.overTitle", "Perioada de probă s-a încheiat")}
            </h2>
            <p className="mt-1.5 text-sm leading-relaxed text-slate-600">
              {t(
                "trial.overBody",
                "Tot ce ai construit este aici și rămâne aici — echipele, antrenorii, sportivii, antrenamentele și prezențele. Le poți vedea în continuare, tu și antrenorii tăi. Ca să puteți adăuga sau modifica din nou, clubul are nevoie de planul Baza."
              )}
            </p>

            <div className="flex flex-wrap items-center gap-3 mt-4">
              <button
                type="button"
                onClick={ask}
                className="px-4 py-2.5 text-sm font-bold text-white transition-colors bg-indigo-600 rounded-xl hover:bg-indigo-700"
              >
                {t("trial.activate", "Activează planul Baza")}
              </button>
              <span className="text-xs text-slate-400">
                {t("trial.noCard", "Fără card. Durează un minut.")}
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const urgent = state === "trial_ending";
  /**
   * A club with no end date has no countdown — and printing 0 for it was the
   * bug: existing clubs had their trial date cleared deliberately, so "0 days
   * left" appeared beside an account that was not expiring at all. No date, no
   * number.
   */
  const hasDeadline = !!trialEndsAt && daysLeft !== null;
  const days = daysLeft ?? 0;

  return (
    <div
      className={`mb-4 rounded-xl border p-4 ${
        urgent ? "bg-amber-50 border-amber-200" : "bg-blue-50 border-blue-200"
      }`}
    >
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <div
            className={`flex items-center justify-center w-10 h-10 rounded-lg flex-shrink-0 ${
              urgent ? "bg-amber-100" : "bg-blue-100"
            }`}
          >
            <svg
              className={`w-5 h-5 ${urgent ? "text-amber-600" : "text-blue-600"}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
          <div className="min-w-0">
            <p className={`text-sm font-semibold ${urgent ? "text-amber-800" : "text-blue-800"}`}>
              {hasDeadline ? (
                <>
                  {t("trial.title", "Perioadă de probă")} — {days}{" "}
                  {days === 1 ? t("trial.dayLeft", "zi rămasă") : t("trial.daysLeft", "zile rămase")}
                </>
              ) : (
                t("trial.titleNoDeadline", "Cont demo")
              )}
            </p>
            <p className={`text-xs mt-0.5 ${urgent ? "text-amber-700" : "text-blue-700"}`}>
              {t("trial.fullFeatures", "Ai toate funcțiile planului Baza, fără limită de antrenori sau sportivi.")}
              {hasDeadline ? ` · ${t("trial.until", "Până pe")} ${formatDate(new Date(trialEndsAt!))}` : ""}
            </p>
          </div>
        </div>

        <button
          type="button"
          onClick={ask}
          className={`px-4 py-2 text-sm font-bold rounded-xl whitespace-nowrap transition-colors ${
            urgent
              ? "bg-amber-600 text-white hover:bg-amber-700"
              : "bg-white text-blue-700 border border-blue-200 hover:bg-blue-100"
          }`}
          title={clubName}
        >
          {t("trial.activate", "Activează planul Baza")}
        </button>
      </div>
    </div>
  );
}
